import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, Download, Upload, Shield, Loader2, CheckCircle2, XCircle, Users, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blank = {
  name: "", description: "", vertical_tags: "", concept_tags: "",
  icon_key: "Sparkles", is_published: false, sort_order: 0,
  payload: { job_statuses: [], custom_fields: [], quick_actions: [], stat_cards: [], service_categories: [], notes: "" },
};

export default function PlatformTemplateAdmin() {
  const { user } = useAuth();
  const isPlatformAdmin = !!user?.is_platform_admin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importJson, setImportJson] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/platform/templates`); setRows(r.data || []); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (isPlatformAdmin) fetchAll(); }, [isPlatformAdmin]);

  // iter149 — Review queue helpers (community submissions awaiting review)
  const communitySubmissions = React.useMemo(
    () => (rows || []).filter((r) => !r.is_published && r.created_by_user_id),
    [rows]
  );
  const [reviewingId, setReviewingId] = useState(null);
  const publishOne = async (row) => {
    setReviewingId(row.id);
    try {
      await axios.patch(`${API}/platform/templates/${row.id}/publish`);
      toast.success(`Published "${row.name}" — tenants of that business type will now see it in the guided tour.`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally { setReviewingId(null); }
  };
  const rejectOne = async (row) => {
    if (!window.confirm(`Reject and delete "${row.name}"? The submitter's data is unaffected — this only removes the submitted template.`)) return;
    setReviewingId(row.id);
    try {
      await axios.delete(`${API}/platform/templates/${row.id}`);
      toast.success(`Rejected "${row.name}".`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally { setReviewingId(null); }
  };

  const openEdit = async (row) => {
    try {
      const r = await axios.get(`${API}/platform/templates/${row.id}`);
      const full = r.data;
      setEdit({ ...full, payload: full.payload || blank.payload });
      setOpen(true);
    } catch { toast.error("Failed to load template"); }
  };

  const save = async () => {
    if (!edit?.name?.trim()) { toast.error("Name required"); return; }
    try {
      const body = {
        name: edit.name, description: edit.description,
        vertical_tags: edit.vertical_tags || (Array.isArray(edit.vertical_tags) ? edit.vertical_tags.join(",") : ""),
        concept_tags: edit.concept_tags || (Array.isArray(edit.concept_tags) ? edit.concept_tags.join(",") : ""),
        icon_key: edit.icon_key || "Sparkles",
        is_published: !!edit.is_published, sort_order: edit.sort_order || 0,
        payload: edit.payload || {},
      };
      // tags may have arrived as arrays from the GET — flatten:
      if (Array.isArray(body.vertical_tags)) body.vertical_tags = body.vertical_tags.join(",");
      if (Array.isArray(body.concept_tags)) body.concept_tags = body.concept_tags.join(",");
      if (edit.id) await axios.put(`${API}/platform/templates/${edit.id}`, body);
      else await axios.post(`${API}/platform/templates`, body);
      toast.success("Saved"); setOpen(false); setEdit(null); fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template? This affects tenants who install it from now on.")) return;
    try { await axios.delete(`${API}/platform/templates/${id}`); toast.success("Deleted"); fetchAll(); }
    catch { toast.error("Delete failed"); }
  };

  const exportOne = async (id, name) => {
    try {
      const r = await axios.get(`${API}/platform/templates/${id}/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${name.replace(/[^a-z0-9]+/gi, "_")}.json`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Exported");
    } catch { toast.error("Export failed"); }
  };

  const doImport = async () => {
    try {
      const parsed = JSON.parse(importJson);
      const r = await axios.post(`${API}/platform/templates/import`, { template_json: parsed });
      toast.success(`Imported "${r.data.name}" (unpublished — review then publish)`);
      setImportOpen(false); setImportJson(""); fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed (invalid JSON?)"); }
  };

  if (!isPlatformAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Shield className="h-12 w-12 text-muted-foreground mb-3" />
        <p className="text-muted-foreground">This area is for the platform owner only.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="platform-template-admin">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-amber-500/10"><Shield className="h-7 w-7 text-amber-600" /></div>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Platform Templates</h1>
            <p className="text-muted-foreground">Curate the starter packs every customer can install during onboarding.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-tpl-btn">
            <Upload className="h-4 w-4 mr-1" /> Import JSON
          </Button>
          <Button onClick={() => { setEdit({ ...blank }); setOpen(true); }} data-testid="add-tpl-btn">
            <Plus className="h-4 w-4 mr-1" /> New template
          </Button>
        </div>
      </div>

      {/* iter149 — Community Review Queue (unpublished, user-submitted) */}
      {communitySubmissions.length > 0 && (
        <Card className="border-2 border-emerald-500/40 bg-emerald-500/5" data-testid="community-review-queue">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-emerald-600" />
              Community Review Queue
              <Badge variant="default" className="bg-emerald-600 text-white ml-2">{communitySubmissions.length}</Badge>
            </CardTitle>
            <CardDescription>
              Templates submitted by tenants via <em>Customize Business → Contribute as Template</em>.
              Publish to make them appear in every new business&apos;s guided tour. Reject to remove.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {communitySubmissions.map((row) => (
              <div
                key={row.id}
                className="flex items-start justify-between gap-3 p-3 rounded-lg border-2 bg-background hover:border-primary/40 transition-colors flex-wrap"
                data-testid={`review-row-${row.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Sparkles className="h-4 w-4 text-primary shrink-0" />
                    <p className="font-semibold text-sm">{row.name}</p>
                    <Badge variant="outline" className="text-[10px]">Draft</Badge>
                  </div>
                  {row.description && (
                    <p className="text-xs text-muted-foreground mt-1">{row.description}</p>
                  )}
                  <p className="text-xs mt-1">
                    <span className="text-muted-foreground">Submitted by </span>
                    <strong>{row.submitter_name || row.submitter_email || "Unknown"}</strong>
                    {row.submitter_email && row.submitter_name && (
                      <span className="text-muted-foreground"> ({row.submitter_email})</span>
                    )}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-2 text-[11px]">
                    {(row.vertical_tags || []).slice(0, 4).map((t) => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                    {row.summary?.job_statuses > 0 && <Badge variant="outline" className="text-[10px]">{row.summary.job_statuses} statuses</Badge>}
                    {row.summary?.custom_fields > 0 && <Badge variant="outline" className="text-[10px]">{row.summary.custom_fields} fields</Badge>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openEdit(row)} title="Inspect payload" data-testid={`review-inspect-${row.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => publishOne(row)}
                    disabled={reviewingId === row.id}
                    className="bg-emerald-600 hover:bg-emerald-700 gap-1"
                    data-testid={`review-publish-${row.id}`}
                  >
                    {reviewingId === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                    Publish
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => rejectOne(row)}
                    disabled={reviewingId === row.id}
                    className="text-destructive border-destructive/40 hover:bg-destructive/10 gap-1"
                    data-testid={`review-reject-${row.id}`}
                  >
                    <XCircle className="h-3.5 w-3.5" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{rows.length} templates</CardTitle>
          <CardDescription>Published rows are immediately available in tenant Onboarding Wizards.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> :
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Contents</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <div className="font-medium">{r.name}</div>
                      <div className="text-xs text-muted-foreground line-clamp-1">{r.description}</div>
                    </TableCell>
                    <TableCell className="text-xs space-x-1">
                      {(r.vertical_tags || []).slice(0, 3).map((t) => <Badge key={t} variant="outline">{t}</Badge>)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.summary?.job_statuses || 0} statuses · {r.summary?.custom_fields || 0} fields · {r.summary?.quick_actions || 0} actions · {r.summary?.stat_cards || 0} cards
                    </TableCell>
                    <TableCell>
                      {r.is_published
                        ? <Badge variant="default" className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">Published</Badge>
                        : <Badge variant="outline">Draft</Badge>}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => exportOne(r.id, r.name)} title="Export JSON"><Download className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          }
        </CardContent>
      </Card>

      {/* Editor dialog */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEdit(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{edit?.id ? "Edit template" : "New template"}</DialogTitle>
            <DialogDescription>Payload is JSON-edited for power-users; the Onboarding Wizard handles installation.</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="grid gap-3 max-h-[70vh] overflow-auto">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="tpl-name" /></div>
                <div><Label>Icon key</Label><Input value={edit.icon_key} onChange={(e) => setEdit({ ...edit, icon_key: e.target.value })} placeholder="Sparkles, Wrench, Calendar, ShoppingBag, PartyPopper" /></div>
              </div>
              <div><Label>Description</Label><Input value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Vertical tags (comma-separated)</Label><Input value={Array.isArray(edit.vertical_tags) ? edit.vertical_tags.join(",") : (edit.vertical_tags || "")} onChange={(e) => setEdit({ ...edit, vertical_tags: e.target.value })} placeholder="hvac,trades,mechanic" /></div>
                <div><Label>Concept tags (comma-separated)</Label><Input value={Array.isArray(edit.concept_tags) ? edit.concept_tags.join(",") : (edit.concept_tags || "")} onChange={(e) => setEdit({ ...edit, concept_tags: e.target.value })} placeholder="field_crews,parts_orders,recurring_jobs" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3 items-end">
                <div><Label>Sort order</Label><Input type="number" value={edit.sort_order || 0} onChange={(e) => setEdit({ ...edit, sort_order: parseInt(e.target.value) || 0 })} /></div>
                <label className="flex items-center gap-2 text-sm cursor-pointer mt-6"><Checkbox checked={!!edit.is_published} onCheckedChange={(v) => setEdit({ ...edit, is_published: !!v })} /> Published (tenant-visible)</label>
              </div>
              <div>
                <Label>Payload (JSON)</Label>
                <Textarea rows={14} className="font-mono text-xs" value={JSON.stringify(edit.payload || {}, null, 2)} onChange={(e) => {
                  try { setEdit({ ...edit, payload: JSON.parse(e.target.value) }); }
                  catch { /* keep raw string in input; user can fix */ }
                }} data-testid="tpl-payload" />
                <p className="text-xs text-muted-foreground mt-1">Keys: job_statuses, custom_fields, quick_actions, stat_cards, service_categories, notes.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-tpl">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Import template (JSON)</DialogTitle><DialogDescription>Paste a previously-exported template. Always imported as unpublished — review then publish.</DialogDescription></DialogHeader>
          <Textarea rows={14} className="font-mono text-xs" value={importJson} onChange={(e) => setImportJson(e.target.value)} placeholder='{"name":"...","payload":{...}}' />
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
            <Button onClick={doImport}>Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
